import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Switch, Tooltip,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import CloseIcon from '@mui/icons-material/Close'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ViewListIcon from '@mui/icons-material/ViewList'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import { FrameworkClauseTree } from './FrameworkClauseTree'
import { RichContent } from './RichContent'
import { useContentScrollArea } from '@/hooks/useContentScrollArea'
import type {
  ApiExternalFramework,
  ApiPractice,
  ApiPracticeArea,
  GetContentBundleResponse,
} from '@/types/api'
import { GuardedLink } from './GuardedLink'

interface FrameworkContentProps {
  framework: ApiExternalFramework
  bundle: GetContentBundleResponse
}

interface ClauseGroup {
  clauseId: number
  clauseKey: string
  topic: string
  practices: Array<{
    mappingId: number
    practice: ApiPractice
    practiceArea: ApiPracticeArea | undefined
    relationshipType: number
    note?: string | null
  }>
}

const SUMMARY_TITLE_ID = 'framework-summary-title'

/**
 * Page header: title, short code, reference link, and the summary modal trigger.
 *
 * Extracted so FrameworkContent can remount it per framework (key={framework.id}),
 * which closes the summary again on navigation without a setState-in-effect.
 */
function FrameworkPageHeader({ framework, bundle }: FrameworkContentProps) {
  const { t } = useTranslation()
  const [summaryOpen, setSummaryOpen] = useState(false)
  const description = framework.description

  return (
    <header className="framework-page-header">
      <div className="framework-page-title-row">
        <h1 className="framework-page-name">{framework.name}</h1>
        <Chip label={framework.shortCode} size="small" className="framework-page-shortcode" />
        {framework.referenceUrl && (
          <a
            href={framework.referenceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="crosswalk-framework-link"
            aria-label={t('content.frameworkOpenReference', { name: framework.name })}
          >
            <OpenInNewIcon fontSize="inherit" />
          </a>
        )}
        {description && (
          <Button
            variant="text"
            // Styled here rather than in index.css so it beats MUI's own class specificity.
            sx={{ fontSize: 13, flexShrink: 0, whiteSpace: 'nowrap' }}
            onClick={() => setSummaryOpen(true)}
            startIcon={<ArticleOutlinedIcon />}
          >
            {t('content.frameworkShowSummary')}
          </Button>
        )}
      </div>
      {description && (
        <Dialog
          open={summaryOpen}
          onClose={() => setSummaryOpen(false)}
          maxWidth="lg"
          fullWidth
          scroll="paper"
          aria-labelledby={SUMMARY_TITLE_ID}
        >
          <DialogTitle
            id={SUMMARY_TITLE_ID}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <span>{framework.name}</span>
            <IconButton
              aria-label={t('common.close')}
              onClick={() => setSummaryOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          {/* rich-tables is applied here too: the dialog portals out of the page wrapper
              that normally provides it, so table styling would otherwise be lost. */}
          <DialogContent className="rich-tables" dividers={false}>
            <div className="framework-page-description">
              <RichContent html={description} bundle={bundle} />
            </div>
          </DialogContent>
        </Dialog>
      )}
      {framework.referenceUrl && (
        <p className="framework-page-reference">
          <span className="framework-page-reference-label">{t('content.frameworkReference')}:</span>{' '}
          <a href={framework.referenceUrl} target="_blank" rel="noopener noreferrer">
            {framework.referenceUrl}
          </a>
        </p>
      )}
    </header>
  )
}

export function FrameworkContent({ framework, bundle }: FrameworkContentProps) {
  const { t } = useTranslation()
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  // Give the scroll area focus on navigation so arrow / page keys scroll the content
  useContentScrollArea(scrollAreaRef, framework.id)
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('tree')

  useEffect(() => {
    if (scrollAreaRef.current) scrollAreaRef.current.scrollTop = 0
  }, [framework.id])

  const clauseGroups = useMemo<ClauseGroup[]>(() => {
    const practiceMap = new Map<number, ApiPractice>(bundle.practices.map((p) => [p.id, p]))
    const practiceAreaMap = new Map<number, ApiPracticeArea>(
      (bundle.practiceAreas ?? []).map((pa) => [pa.id, pa]),
    )
    const clauseMap = new Map(
      (bundle.externalFrameworkClauses ?? [])
        .filter((c) => c.externalFrameworkId === framework.id)
        .map((c) => [c.id, c]),
    )

    const groups = new Map<number, ClauseGroup>()
    for (const m of bundle.practiceCrosswalkMappings ?? []) {
      if (m.externalFrameworkId !== framework.id) continue
      const clause = clauseMap.get(m.externalFrameworkClauseId)
      const practice = practiceMap.get(m.practiceId)
      if (!clause || !practice) continue

      let group = groups.get(clause.id)
      if (!group) {
        group = { clauseId: clause.id, clauseKey: clause.clauseKey, topic: clause.topic, practices: [] }
        groups.set(clause.id, group)
      }
      group.practices.push({
        mappingId: m.id,
        practice,
        practiceArea: practiceAreaMap.get(practice.practiceAreaId),
        relationshipType: m.relationshipType,
        note: m.note,
      })
    }

    for (const group of groups.values()) {
      group.practices.sort(
        (a, b) =>
          (a.practiceArea?.sortOrder ?? 0) - (b.practiceArea?.sortOrder ?? 0) ||
          a.practice.sortOrder - b.practice.sortOrder,
      )
    }

    return [...groups.values()].sort((a, b) =>
      a.clauseKey.localeCompare(b.clauseKey, undefined, { numeric: true, sensitivity: 'base' }),
    )
  }, [bundle, framework.id])

  return (
    <div className="framework-content rich-tables">
      <div className="content-scroll-area" ref={scrollAreaRef} tabIndex={0}>
        <div className="framework-page">
          <FrameworkPageHeader key={framework.id} framework={framework} bundle={bundle} />

          <div className="framework-view-toggle">
            <span
              className={`framework-view-toggle-label${viewMode === 'list' ? ' framework-view-toggle-label--active' : ''}`}
            >
              <ViewListIcon fontSize="inherit" />
              {t('content.frameworkViewListLabel')}
            </span>
            <Switch
              size="small"
              checked={viewMode === 'tree'}
              onChange={(e) => setViewMode(e.target.checked ? 'tree' : 'list')}
              inputProps={{ 'aria-label': t('content.frameworkViewToggleAria') }}
            />
            <span
              className={`framework-view-toggle-label${viewMode === 'tree' ? ' framework-view-toggle-label--active' : ''}`}
            >
              <AccountTreeIcon fontSize="inherit" />
              {t('content.frameworkViewTreeLabel')}
            </span>
          </div>

          {viewMode === 'tree' ? (
            <section className="framework-page-mappings">
              <FrameworkClauseTree key={framework.id} framework={framework} bundle={bundle} />
            </section>
          ) : (
          <section className="framework-page-mappings">
            {clauseGroups.length === 0 ? (
              <p className="empty-section">{t('content.noMappings')}</p>
            ) : (
              <div className="crosswalk-groups">
                {clauseGroups.map((group) => (
                  <div key={group.clauseId} className="crosswalk-framework-group">
                    <div className="framework-page-clause-header">
                      <code className="crosswalk-clause-key">{group.clauseKey}</code>
                      <span className="crosswalk-clause-topic">{group.topic}</span>
                    </div>
                    <ul className="crosswalk-clause-list">
                      {group.practices.map((row) => {
                        const relKey = String(row.relationshipType) as '1' | '2' | '3' | '4' | '5' | '6'
                        const relName = t(`content.crosswalkRelationshipTypes.${relKey}.name`, { defaultValue: `Type ${row.relationshipType}` })
                        const relDesc = t(`content.crosswalkRelationshipTypes.${relKey}.description`, { defaultValue: '' })
                        return (
                          <li key={row.mappingId} className="crosswalk-clause-item">
                            <Tooltip
                              title={
                                relDesc ? (
                                  <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{relName}</div>
                                    <div>{relDesc}</div>
                                  </div>
                                ) : relName
                              }
                              placement="top"
                              arrow
                              slotProps={{ tooltip: { sx: { maxWidth: 320 } } }}
                            >
                              <span className={`crosswalk-relationship-badge crosswalk-relationship-badge--rel${relKey}`}>
                                {relName}
                                <HelpOutlineIcon className="crosswalk-rel-info-icon" fontSize="inherit" />
                              </span>
                            </Tooltip>
                            <GuardedLink to={`/practice/${row.practice.id}`} className="clause-practice-link">
                              <span className="clause-practice-name">{row.practice.displayName}</span>
                            </GuardedLink>
                            {row.note && <span className="crosswalk-note">{row.note}</span>}
                          </li>
                        )
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
          )}
        </div>
      </div>
    </div>
  )
}
